'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * DeferredMount
 *
 * Delays mounting (and therefore downloading/executing the JS for) its
 * children until the wrapper scrolls near the viewport. This is distinct
 * from `next/dynamic({ ssr: false })`, which only defers *when the chunk is
 * requested relative to the initial bundle* — without this, dynamically
 * imported below-the-fold sections still mount (and execute) immediately
 * after hydration, competing with the critical rendering path for main
 * thread time and contributing to Total Blocking Time.
 *
 * Usage:
 *   <DeferredMount>
 *     <HeavySection />
 *   </DeferredMount>
 *
 * Once rendered, children stay mounted (no unmount-on-scroll-away) to avoid
 * remount flicker/layout thrash if the user scrolls back up.
 */
export default function DeferredMount({
    children,
    fallback = null,
    rootMargin = '600px 0px',
    minHeight,
}) {
    const ref = useRef(null);
    const [shouldRender, setShouldRender] = useState(false);

    useEffect(() => {
        if (shouldRender) return;
        const node = ref.current;
        if (!node || typeof IntersectionObserver === 'undefined') {
            // No observer support (or no node to observe) — render immediately
            // rather than hiding content forever.
            setShouldRender(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setShouldRender(true);
                }
            },
            { rootMargin },
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [shouldRender, rootMargin]);

    if (shouldRender) {
        // Once visible, render children directly — no need to keep the
        // wrapper div around once we're not gating anything anymore.
        return children;
    }

    // Reserve vertical space matching the expected content height to prevent
    // layout shift (CLS) when the children eventually mount. Without this,
    // the zero-height placeholder causes content below to jump when the
    // deferred section appears.
    const placeholderStyle = minHeight ? { minHeight } : undefined;

    return (
        <div
            ref={ref}
            style={placeholderStyle}
            aria-hidden={fallback ? undefined : 'true'}
        >
            {fallback}
        </div>
    );
}
